import { holds, type Holder, type Requirement } from "../lib/drill/targets";

/**
 * The navigation model — one table, read by the pane (§5), the breadcrumb (§5)
 * and the router.
 *
 * §5's sizing note for this console: **twenty-three bounded contexts do not
 * become twenty-three destinations.** They are grouped, and the pane is measured
 * against the widest MONGOLIAN label, not the English one — an English label is
 * typically 20–30 % shorter than its Mongolian counterpart, so a pane sized to
 * `en` clips «Түдгэлзүүлэх» (§4.3).
 *
 * Every destination names the capability its reader requires. A destination the
 * operator cannot open is not drawn — a nav item leading to a 403 is the same
 * defect §7.3 describes for a drill, one level up.
 */

export interface NavItem {
  readonly path: string;
  /** The Lingui message id. The pane translates; the export filenames do not. */
  readonly label: string;
  readonly requires: Requirement;
  /** Sub-pages that keep this item selected. */
  readonly children?: readonly { readonly path: string; readonly label: string }[];
}

export interface NavGroup {
  readonly id: string;
  readonly label: string;
  readonly items: readonly NavItem[];
}

export const NAVIGATION: readonly NavGroup[] = [
  {
    id: "overview",
    label: "nav.group.overview",
    items: [
      // Every signed-in operator holds `profile.manage`; the dashboard's own
      // cards are each gated by the capability of the register they summarise.
      { path: "/", label: "nav.dashboard", requires: { permission: "profile.manage" } },
      // `analytics.read` is on ADMIN **and** FINANCE, unlike the other `admin.*`
      // permissions, because this surface is read-only over aggregates: a
      // merchant league table moves nothing, and withholding it would only mean
      // an admin reading the same figures out of the order register one page at
      // a time.
      { path: "/analytics", label: "nav.analytics", requires: { permission: "analytics.read" } },
    ],
  },
  {
    id: "marketplace",
    label: "nav.group.marketplace",
    items: [
      {
        path: "/merchants",
        label: "nav.merchants",
        requires: { permission: "admin.merchants.approve" },
        children: [{ path: "/merchants/commission", label: "nav.commission" }],
      },
      {
        path: "/catalog",
        label: "nav.catalog",
        requires: { permission: "catalog.read" },
        children: [{ path: "/catalog/taxonomy", label: "nav.taxonomy" }],
      },
      { path: "/orders", label: "nav.orders", requires: { permission: "admin.orders.read" } },
      { path: "/disputes", label: "nav.disputes", // `@Roles('ADMIN')` on the controller; `admin.refunds.issue` is granted to no role.
        requires: { role: "ADMIN" } },
    ],
  },
  {
    id: "operations",
    label: "nav.group.operations",
    items: [{ path: "/dispatch", label: "nav.dispatch", requires: { permission: "admin.orders.read" } }],
  },
  /**
   * The three desks where a person decides about a person.
   *
   * They are grouped rather than scattered because that is how the work is
   * done: an ops lead answering «why was my order refused» opens the risk desk
   * from the ticket, and a moderator taking a review down has usually just read
   * a complaint about it. Splitting them across «marketplace» and «operations»
   * would put two halves of one motion in two places in the pane.
   */
  {
    id: "trust",
    label: "nav.group.trust",
    items: [
      {
        // `fraud.read` is on ADMIN and FINANCE; `fraud.decide` is FINANCE's
        // alone, so an ADMIN sees the desk with its write controls refused
        // rather than not seeing the desk. The rules and lists pages are
        // children so the pane stays one line per destination.
        path: "/risk",
        label: "nav.risk",
        requires: { permission: "fraud.read" },
        children: [
          { path: "/risk/rules", label: "nav.riskRules" },
          { path: "/risk/lists", label: "nav.riskLists" },
        ],
      },
      {
        // `support.read` is on ADMIN and FINANCE; `support.write` is ADMIN's, so
        // a finance reader can see WHY a ticket is late without answering a
        // customer as Shoppy.
        path: "/support",
        label: "nav.support",
        requires: { permission: "support.read" },
        children: [
          { path: "/support/nps", label: "nav.supportNps" },
          { path: "/support/articles", label: "nav.supportArticles" },
        ],
      },
      { path: "/moderation", label: "nav.moderation", requires: { permission: "reviews.moderate" } },
    ],
  },
  /**
   * The three programmes that spend money to bring people back. Each is a dated
   * policy record over a register of what the policy did (§0), which is why
   * they read as one group rather than as three unrelated screens.
   */
  {
    id: "growth",
    label: "nav.group.growth",
    items: [
      { path: "/loyalty", label: "nav.loyalty", requires: { permission: "loyalty.read" } },
      {
        // `reviews-admin.controller.ts` gates both affiliate registers on
        // `@Roles('ADMIN','FINANCE')` and there is no permission spanning the
        // two — `reviews.affiliate.decide` is FINANCE's and is the WRITE. Naming
        // one role would hide the register from people the api admits.
        path: "/affiliate",
        label: "nav.affiliate",
        requires: { anyRole: ["ADMIN", "FINANCE"] },
      },
      {
        // The e-ticketing reads this console can reach are the PUBLIC ones; the
        // organiser surface needs `tickets.manage`, which no staff role holds.
        // Gated on the order-reading permission because that is what the desk
        // is for: what is on sale, and how it is selling.
        path: "/events",
        label: "nav.events",
        requires: { permission: "admin.orders.read" },
      },
    ],
  },
  {
    id: "finance",
    label: "nav.group.finance",
    items: [
      {
        path: "/finance/payables",
        label: "nav.payables",
        requires: { permission: "payouts.approve" },
        children: [{ path: "/finance/payouts", label: "nav.payouts" }],
      },
      {
        path: "/finance/trial-balance",
        label: "nav.trialBalance",
        requires: { permission: "ledger.read" },
        children: [{ path: "/finance/tie-outs", label: "nav.tieOuts" }],
      },
      { path: "/tax/issuers", label: "nav.taxIssuers", requires: { permission: "admin.merchants.approve" } },
    ],
  },
  {
    id: "system",
    label: "nav.group.system",
    items: [
      {
        /**
         * The admin bounded context's four screens hang off Settings as
         * children rather than becoming four destinations of their own: §5's
         * sizing note is that twenty-three bounded contexts do not become
         * twenty-three entries, and «flags», «dated settings», «audit» and
         * «system status» are four faces of one question — what this platform
         * is configured to do and who configured it.
         */
        path: "/settings",
        label: "nav.settings",
        requires: { permission: "admin.accounts.manage" },
        children: [
          { path: "/settings/flags", label: "nav.flags" },
          { path: "/settings/dated", label: "nav.datedSettings" },
          { path: "/settings/audit", label: "nav.audit" },
          { path: "/settings/system", label: "nav.system" },
        ],
      },
    ],
  },
];

/**
 * Which nav item a path belongs to. Longest match wins, so `/merchants/commission`
 * selects «Худалдагч» rather than falling back to the dashboard's `/`.
 */
export function activeItem(pathname: string): NavItem | null {
  let best: NavItem | null = null;
  for (const group of NAVIGATION) {
    for (const item of group.items) {
      for (const candidate of [item.path, ...(item.children ?? []).map((child) => child.path)]) {
        const matches = candidate === "/" ? pathname === "/" : pathname.startsWith(candidate);
        if (matches && (best === null || candidate.length > best.path.length)) best = item;
      }
    }
  }
  return best;
}

/**
 * The breadcrumb. §5.1: **the breadcrumb already carries the page's name**, which
 * is why a register that IS the page draws no heading — a title band under it
 * spends 60px repeating a word that is already on screen.
 */
export function breadcrumbFor(pathname: string): { label: string; path: string }[] {
  const item = activeItem(pathname);
  if (!item) return [];

  const group = NAVIGATION.find((candidate) => candidate.items.includes(item));
  const trail: { label: string; path: string }[] = [];
  if (group) trail.push({ label: group.label, path: item.path });
  trail.push({ label: item.label, path: item.path });

  const child = item.children?.find((candidate) => pathname.startsWith(candidate.path));
  if (child && child.path !== item.path) trail.push({ label: child.label, path: child.path });
  return trail;
}

/** Everything the operator may actually open, groups with no items removed. */
export function visibleNavigation(holder: Holder): NavGroup[] {
  return NAVIGATION.map((group) => ({
    ...group,
    items: group.items.filter((item) => holds(holder, item.requires)),
  })).filter((group) => group.items.length > 0);
}
